import { useMemo } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { useSeo, SITE_ORIGIN } from "@/hooks/useSeo";
import { siteInfo, stats, voices } from "@/mocks/archive";

const principles = [
  {
    icon: "ri-scales-3-line",
    title: "记录与推断分开",
    desc: "凡是能追溯到调查报告、学术研究或原始记录的，我们标注为记录；凡是仍存在多种解释的，我们如实说明分歧，不替读者下结论。",
  },
  {
    icon: "ri-time-line",
    title: "时间优先于叙述",
    desc: "先确定事件在时间上的先后关系，再组织文字。顺序错了，因果关系就会跟着错，这是我们最先要避免的问题。",
  },
  {
    icon: "ri-refresh-line",
    title: "定期复核与勘误",
    desc: "档案条目每季度复核一次。发现数据更新、结论修正或读者指出的错误时，会直接在原文中更新并记录修订时间。",
  },
];

const positions = [
  {
    title: "我们是谁",
    desc: "暗线档案是一个专注于暗黑史脉络整理的内容栏目，由一群对历史、考古与工程事故感兴趣的编辑共同维护。我们不生产未经核实的爆料，而是把已经公开的零散信息重新编年。",
  },
  {
    title: "为谁而写",
    desc: "为需要快速建立事件脉络的读者而写：备课的教师、写稿的创作者、做功课的学生，以及单纯想把一件复杂的事搞明白的人。",
  },
  {
    title: "不做什么",
    desc: "不针对在世个人发布未经证实的负面指控，不虚构具体地址、备案信息与权威资质，不用情绪化标题替代事实本身。",
  },
];

export default function About() {
  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于暗线档案",
        url: `${SITE_ORIGIN}/about`,
        inLanguage: "zh-CN",
        description:
          "暗线档案的栏目定位、内容原则与联系方式介绍。我们以时间轴为核心整理暗黑史脉络。",
        isPartOf: { "@type": "WebSite", name: siteInfo.name, url: SITE_ORIGIN },
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: siteInfo.name,
        url: SITE_ORIGIN,
        email: siteInfo.email,
        description: siteInfo.description,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
          { "@type": "ListItem", position: 2, name: "关于我们", item: `${SITE_ORIGIN}/about` },
        ],
      },
    ],
    []
  );

  useSeo({
    title: "关于我们 | 暗线档案的内容原则与栏目定位",
    description:
      "了解暗线档案的栏目定位、编年方法与内容原则。我们以时间轴为核心整理暗黑史脉络，区分已确认记录与开放推断，并坚持定期复核与公开勘误。",
    keywords: "关于暗线档案,内容原则,暗黑史编年,档案栏目定位,事件时间线整理",
    path: "/about",
    jsonLd,
  });

  return (
    <SiteLayout>
      <section className="w-full px-4 md:px-8 pt-28 md:pt-32 pb-12 border-b border-background-200">
        <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
        <h1 className="mt-6 font-heading text-3xl md:text-5xl font-black text-foreground-950">
          关于暗线档案
        </h1>
        <p className="mt-4 max-w-3xl text-sm md:text-base leading-relaxed text-foreground-500">
          {siteInfo.description}
        </p>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-16 border-b border-background-200">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="w-full h-72 md:h-96 overflow-hidden rounded-lg border border-background-200">
            <img
              src="https://readdy.ai/api/search-image?query=Quiet%20archive%20room%20in%20deep%20shadow%20with%20tall%20wooden%20shelves%20of%20bound%20volumes%20and%20boxed%20files%2C%20a%20single%20warm%20desk%20lamp%20casting%20amber%20light%20across%20a%20long%20table%2C%20dust%20motes%20in%20the%20beam%2C%20restrained%20moody%20atmosphere%2C%20muted%20brown%20and%20charcoal%20palette&width=1200&height=800&seq=anxian-about&orientation=landscape"
              alt="暗线档案的整理工作室：深色档案架与暖光书桌"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div>
            <span className="text-xs tracking-[0.3em] uppercase text-primary-400">Since {siteInfo.est}</span>
            <h2 className="mt-3 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              把碎片化的信息还原成一条线
            </h2>
            <p className="mt-5 text-sm md:text-base leading-loose text-foreground-600">
              很多事件本身并不神秘，令人困惑的往往是次序。同一件事在不同资料里出现时，年份、地点甚至参与方都可能不一致。
              我们从这些矛盾入手，先重建时间框架，再讨论因果，最终形成可以被顺序阅读的档案。
            </p>
            <p className="mt-4 text-sm md:text-base leading-loose text-foreground-600">
              目前栏目已覆盖从古代到当代的多个纪元，并持续补充新的条目。每一条档案都保留开放的疑问，而不是假装一切都已有定论。
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/archive"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md text-sm whitespace-nowrap cursor-pointer bg-primary-500 text-background-950 hover:bg-primary-400 transition-colors"
              >
                <i className="ri-folder-history-line" aria-hidden="true"></i>
                浏览全部档案
              </Link>
              <a
                href={`mailto:${siteInfo.email}`}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md text-sm whitespace-nowrap cursor-pointer border border-background-300 text-foreground-800 hover:border-primary-700 transition-colors"
              >
                <i className="ri-mail-line" aria-hidden="true"></i>
                联系我们
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-16 border-b border-background-200 bg-background-100">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
          网站定位
        </h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {positions.map((item) => (
            <div
              key={item.title}
              className="p-6 rounded-lg border border-background-200 bg-background-50"
            >
              <h3 className="font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-500">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-16 border-b border-background-200">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
          内容原则
        </h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {principles.map((item) => (
            <div key={item.title} className="flex flex-col">
              <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-950 text-primary-400">
                <i className={`${item.icon} text-2xl`} aria-hidden="true"></i>
              </span>
              <h3 className="mt-5 font-heading text-lg font-bold text-foreground-950">
                {item.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-500">{item.desc}</p>
            </div>
          ))}
        </div>

        <dl className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-background-200 pt-10">
          {stats.map((item) => (
            <div key={item.label}>
              <dt className="text-xs text-foreground-500">{item.label}</dt>
              <dd className="mt-2 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                {item.value}
                <span className="ml-1 text-sm font-normal text-primary-400">{item.unit}</span>
              </dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-16 border-b border-background-200">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
          读者口碑
        </h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {voices.map((voice) => (
            <blockquote
              key={voice.name}
              className="flex flex-col h-full p-6 rounded-lg border border-background-200 bg-background-100"
            >
              <i className="ri-double-quotes-l text-2xl text-primary-500/70" aria-hidden="true"></i>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-foreground-600">{voice.text}</p>
              <footer className="mt-5 pt-4 border-t border-background-200">
                <span className="block font-heading text-sm font-semibold text-foreground-900">
                  {voice.name}
                </span>
                <span className="mt-0.5 block text-xs text-foreground-500">{voice.role}</span>
              </footer>
            </blockquote>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <span className="text-xs tracking-[0.3em] uppercase text-secondary-300">Contact</span>
            <h2 className="mt-3 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              发现错误，或想补充线索？
            </h2>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
              我们欢迎指出档案中的事实错误、年代偏差或遗漏的公开资料。来信请尽量附上可核对的出处，我们会在复核后更新条目并记录修订。
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-100 p-8">
            <a
              href={`mailto:${siteInfo.email}`}
              className="flex items-center gap-3 cursor-pointer group"
            >
              <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-950 text-primary-400">
                <i className="ri-mail-send-line text-2xl" aria-hidden="true"></i>
              </span>
              <span>
                <span className="block text-xs text-foreground-500">编辑邮箱</span>
                <span className="mt-1 block text-sm md:text-base text-foreground-900 group-hover:text-primary-400 transition-colors">
                  {siteInfo.email}
                </span>
              </span>
            </a>
            <p className="mt-6 text-xs leading-relaxed text-foreground-500">
              我们通常在三个工作日内回复与内容勘误相关的邮件。涉及版权或授权事宜，请在标题中注明。
            </p>
            <a
              href="#subscribe"
              className="mt-6 inline-flex items-center gap-2 text-sm text-primary-400 hover:text-primary-300 cursor-pointer transition-colors"
            >
              <i className="ri-notification-3-line" aria-hidden="true"></i>
              订阅更新汇总
            </a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}